import React from 'react';
import styles from './InteractiveMap.module.css';

interface CompressorUnit {
  id: string;
  x: number;
  y: number;
  status: 'operational' | 'warning' | 'critical';
}

const compressorUnits: CompressorUnit[] = [
  { id: 'C-204', x: 20, y: 30, status: 'operational' },
  { id: 'C-107', x: 45, y: 50, status: 'operational' },
  { id: 'C-315', x: 70, y: 25, status: 'warning' },
  { id: 'C-189', x: 35, y: 70, status: 'operational' },
  { id: 'C-421', x: 80, y: 60, status: 'operational' },
  { id: 'C-268', x: 55, y: 80, status: 'critical' },
  { id: 'C-152', x: 15, y: 65, status: 'operational' },
  { id: 'C-397', x: 90, y: 40, status: 'warning' },
];

const InteractiveMap: React.FC = () => {

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'var(--color-success)';
      case 'warning':
        return 'var(--color-gold)';
      case 'critical':
        return 'var(--color-error)';
      default:
        return 'var(--color-border)';
    }
  };

  return (
    <div className={styles.mapContainer}>
      <div className={styles.mapOverlay}>
        <div className={styles.mapLegendItem}>
          <div className={`${styles.mapLegendDot} ${styles.green}`}></div>
          <span>Operational</span>
        </div>
        <div className={styles.mapLegendItem}>
          <div className={`${styles.mapLegendDot} ${styles.yellow}`}></div>
          <span>Needs Attention</span>
        </div>
        <div className={styles.mapLegendItem}>
          <div className={`${styles.mapLegendDot} ${styles.red}`}></div>
          <span>Critical</span>
        </div>
      </div>

      <svg
        className={styles.mapSvg}
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Grid background */}
        <defs>
          <pattern id="grid" width="5" height="5" patternUnits="userSpaceOnUse">
            <path
              d="M 5 0 L 0 0 0 5"
              fill="none"
              stroke="var(--color-border-subtle)"
              strokeWidth="0.1"
            />
          </pattern>

          {/* Glow filters for each status */}
          <filter id="glow-green">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="glow-yellow">
            <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="glow-red">
            <feGaussianBlur stdDeviation="0.8" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        <rect width="100" height="100" fill="url(#grid)" />

        {/* Connection lines between nearby units */}
        <g opacity="0.15">
          <line x1="20" y1="30" x2="45" y2="50" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="45" y1="50" x2="70" y2="25" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="45" y1="50" x2="35" y2="70" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="70" y1="25" x2="90" y2="40" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="80" y1="60" x2="90" y2="40" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="35" y1="70" x2="15" y2="65" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="35" y1="70" x2="55" y2="80" stroke="var(--color-gold)" strokeWidth="0.1" />
          <line x1="80" y1="60" x2="55" y2="80" stroke="var(--color-gold)" strokeWidth="0.1" />
        </g>

        {/* Compressor nodes */}
        {compressorUnits.map((unit) => {
          const color = getStatusColor(unit.status);
          const filter = unit.status === 'critical' ? 'url(#glow-red)' :
                        unit.status === 'warning' ? 'url(#glow-yellow)' :
                        'url(#glow-green)';

          return (
            <g key={unit.id} className={styles.mapNode}>
              {/* Outer pulse ring for critical/warning */}
              {(unit.status === 'critical' || unit.status === 'warning') && (
                <circle
                  cx={unit.x}
                  cy={unit.y}
                  r="2.5"
                  fill="none"
                  stroke={color}
                  strokeWidth="0.2"
                  opacity="0.6"
                  className={unit.status === 'critical' ? styles.pulseCritical : styles.pulseWarning}
                />
              )}

              {/* Main node */}
              <circle
                cx={unit.x}
                cy={unit.y}
                r="1.2"
                fill={color}
                filter={filter}
                className={styles.nodeCircle}
              />

              {/* Inner highlight */}
              <circle
                cx={unit.x}
                cy={unit.y}
                r="0.6"
                fill="white"
                opacity="0.3"
              />

              {/* Unit label */}
              <text
                x={unit.x}
                y={unit.y - 2.5}
                className={styles.nodeLabel}
                textAnchor="middle"
                fill="var(--color-text-primary)"
                fontSize="1.2"
                fontWeight="500"
              >
                {unit.id}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default InteractiveMap;
